import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search, User, LogOut, Shield, Moon, Sun, Compass, Sparkles, Users, LayoutDashboard, Wrench, Plus, MessageCircle, Layers, Briefcase, CalendarDays, Heart } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { MessageBell } from '@/components/messages/MessageBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { DiscoverMegaMenu, CreateMegaMenu, CommunityMegaMenu } from './MegaMenu';
import { SubmitMenu } from './SubmitMenu';
import { trackPlaybook } from '@/lib/playbookAnalytics';

const NewBadge = () => (
  <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide bg-playbook-yellow text-playbook-ink px-1.5 py-0.5 rounded-full">New</span>
);

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAdmin, isLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Lock background scroll and hide the chat launcher while the mobile menu is open
  useEffect(() => {
    document.body.classList.toggle('mobile-menu-open', mobileMenuOpen);
    return () => document.body.classList.remove('mobile-menu-open');
  }, [mobileMenuOpen]);

  return (
    <header className="sticky top-0 z-50 glass">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <span className="text-xl font-bold text-primary-foreground">AI</span>
            </div>
            <span className="font-display font-bold text-xl">ToolHub</span>
            <span className="text-[10px] font-semibold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full uppercase tracking-wide hidden xl:inline">Beta</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <NavigationMenu>
              <NavigationMenuList>
                {/* Discover Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent px-2 xl:px-4">
                    <Compass className="h-4 w-4 mr-1 hidden xl:block" />
                    Discover
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <DiscoverMegaMenu />
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Create Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent px-2 xl:px-4">
                    <Sparkles className="h-4 w-4 mr-1 hidden xl:block" />
                    Create
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <CreateMegaMenu />
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Community Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent px-2 xl:px-4">
                    <Users className="h-4 w-4 mr-1 hidden xl:block" />
                    Community
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <CommunityMegaMenu />
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            <Link
              to="/ai-tools-playbook"
              onClick={() => trackPlaybook('playbook_promo_click', 'header')}
              className="hidden xl:inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              AI Playbook<NewBadge />
            </Link>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-1 xl:gap-3">
            <Button variant="ghost" size="sm" asChild className="hidden xl:inline-flex">
              <Link to="/premium?tab=ads">Advertise</Link>
            </Button>
            <SubmitMenu />
            <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" asChild>
              <Link to="/search" aria-label="Search">
                <Search className="h-5 w-5" />
              </Link>
            </Button>
            {user && <MessageBell />}
            {user && <NotificationBell />}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="User menu">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2">
                      <LayoutDashboard className="h-4 w-4" />
                      My Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-tools" className="flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      My Tools
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-stack" className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      My AI Stacks
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/portfolio" className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      Subscription Tracker
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild className="gap-2">
                <Link to="/auth">
                  <Sparkles className="h-4 w-4" />
                  Join Free
                </Link>
              </Button>
            )}
          </div>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-1">
            {!isLoading && (user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Account menu">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2">
                      <LayoutDashboard className="h-4 w-4" />
                      My Account
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/favorites" className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Favorites
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/my-stack" className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      My Stack
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild size="sm">
                <Link to="/auth?mode=signup">Sign Up</Link>
              </Button>
            ))}
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden mobile-nav-panel animate-fade-in">
            <div className="flex flex-col gap-4 min-h-0">
              {/* Auth actions for signed-out visitors */}
              {!isLoading && !user && (
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" asChild className="h-11">
                    <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>Log In</Link>
                  </Button>
                  <Button asChild className="h-11">
                    <Link to="/auth?mode=signup" onClick={() => setMobileMenuOpen(false)}>Create Account</Link>
                  </Button>
                </div>
              )}
              {/* Prominent Submit Tool Button */}
              <SubmitMenu variant="default" hover={false} className="w-full h-10" onSelect={() => setMobileMenuOpen(false)} />
              <Button variant="outline" asChild>
                <Link to="/premium?tab=ads" onClick={() => setMobileMenuOpen(false)}>Advertise</Link>
              </Button>
              <Link
                to="/ai-tools-playbook"
                onClick={() => { trackPlaybook('playbook_promo_click', 'mobile_menu'); setMobileMenuOpen(false); }}
                className="inline-flex items-center font-medium hover:text-primary transition-colors"
              >
                AI Playbook<NewBadge />
              </Link>
              {/* Discover Section */}
              <div className="space-y-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Discover</span>
                <div className="flex flex-col gap-2 pl-2">
                  <Link to="/tools" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Explore All Tools
                  </Link>
                  <Link to="/categories" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Categories
                  </Link>
                  <Link to="/skills" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    AI Skills
                  </Link>
                  <Link to="/news" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    AI News
                  </Link>
                  <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    AI Jobs
                  </Link>
                  <Link to="/events" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Events
                  </Link>
                  <Link to="/health" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Tool Health
                  </Link>
                </div>
              </div>

              {/* Create Section */}
              <div className="space-y-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Create</span>
                <div className="flex flex-col gap-2 pl-2">
                  <Link to="/carousel-builder" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Carousel Builder
                  </Link>
                  <Link to="/social-cards" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Social Media Tools
                  </Link>
                  <Link to="/carousel-templates" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Carousel Templates
                  </Link>
                  <Link to="/templates" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    AI Workflow Templates
                  </Link>
                  <Link to="/stack-builder" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Stack Builder
                  </Link>
                  <Link to="/my-stack" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    My AI Stacks
                  </Link>
                  <Link to="/income-blueprints" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Income Blueprints
                  </Link>
                  <Link to="/submit" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Submit Tool
                  </Link>
                </div>
              </div>

              {/* Community Section */}
              <div className="space-y-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Community</span>
                <div className="flex flex-col gap-2 pl-2">
                  <Link to="/marketplace" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Vibe Coders
                  </Link>
                  <Link to="/leaderboard" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Leaderboard
                  </Link>
                  <Link to="/battles" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Tool Battles
                  </Link>
                  <Link to="/roast-arena" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Roast Arena
                  </Link>
                  <Link to="/referrals" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Referrals
                  </Link>
                  <Link to="/post-job" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Post a Job
                  </Link>
                  <Link to="/events/submit" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Submit Event
                  </Link>
                </div>
              </div>

              <div className="border-t pt-4 flex flex-col gap-2">
                <Link to="/search" className="text-muted-foreground hover:text-foreground transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Search
                </Link>
                {user && (
                  <Link to="/marketplace/messages" className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                    <MessageCircle className="h-4 w-4" />
                    Messages
                  </Link>
                )}
                {user ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Dashboard
                    </Link>
                    <Link 
                      to="/my-tools" 
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Tools
                    </Link>
                    <Link 
                      to="/my-stack" 
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My AI Stacks
                    </Link>
                    {isAdmin && (
                      <Link 
                        to="/admin" 
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <button 
                      onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                      className="text-left text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link 
                    to="/auth" 
                    className="inline-flex items-center gap-2 text-primary font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Sparkles className="h-4 w-4" />
                    Join Free
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
